import { useState, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import CheckListOptions from "./CheckListOptions";
import {
  Box,
  Button,
  Popper,
  Paper,
  ClickAwayListener,
  Typography,
  Divider,
  IconButton,
} from "@mui/material";

const CheckboxDropdown = ({
  options,
  title,
  selectedFilters,
  setSelectedFilters,
}: {
  options: any[];
  title: string;
  selectedFilters: string[];
  setSelectedFilters: React.Dispatch<React.SetStateAction<string[]>>;
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [tempSelectedFilters, setTempSelectedFilters] =
    useState<string[]>(selectedFilters);

  // Keep temp state in sync with parent (e.g. when clearFilters resets the parent
  // while the dropdown is closed — without this, clicking Save re-applies old values).
  useEffect(() => {
    setTempSelectedFilters(selectedFilters);
  }, [selectedFilters]);

  const handleToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
    setTempSelectedFilters(selectedFilters);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    setTempSelectedFilters([]);
    setSelectedFilters([]);
  };

  const handleSave = () => {
    setSelectedFilters(tempSelectedFilters); // Apply changes
    handleClose();
  };

  const buttonTitle =
    selectedFilters.length === 0 ? title : `${selectedFilters.join(", ")} `;

  return (
    <Box>
      <Button
        variant="outlined"
        onClick={handleToggle}
        sx={{
          border:
            selectedFilters.length > 0
              ? "2px solid #1565c0"
              : "1px solid #bdbdbd",
          borderRadius: "50px",
          px: 2,
          textTransform: "capitalize",
          color: selectedFilters.length > 0 ? "#1565c0" : "black",
          display: "flex",
          alignItems: "center",
          backgroundColor: selectedFilters.length > 0 ? "#e3f2fd" : "white",
          "&:hover": { border: "1px solid black", backgroundColor: "white" },
          "&:focus": {
            border: "2px solid #1565c0",
            backgroundColor: "#e3f2fd",
            color: "#1565c0",
          },
        }}
      >
        {buttonTitle}
        {selectedFilters.length > 0 && (
          <IconButton
            component="span"
            size="small"
            onClick={handleClear}
            sx={{ ml: 1, p: 0, color: "#1565c0" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Button>

      <Popper
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement="bottom-start"
        sx={{ zIndex: 999 }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper
            sx={{
              width: { xs: "200px", md: "320px" },
              boxShadow: 3,
              borderRadius: "10px",
              px: "20px",
              py: "10px",
            }}
          >
            <CheckListOptions
              maxHeight="600px"
              options={options}
              selectedFilters={tempSelectedFilters} // Pass temp state
              setSelectedFilters={setTempSelectedFilters} // Update temp state
            />

            <Divider sx={{ my: 1 }} />

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 1,
              }}
            >
              <Typography
                onClick={handleClear}
                sx={{
                  textTransform: "capitalize",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                Clear
              </Typography>
              <Button
                variant="contained"
                sx={{
                  borderRadius: "10px",
                  backgroundColor: "black",
                  color: "white",
                  px: 2,
                  "&:hover": { backgroundColor: "#333" },
                }}
                onClick={handleSave} // Apply changes only on Save
              >
                Save
              </Button>
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

export default CheckboxDropdown;
